import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Container, Row, Button } from 'reactstrap';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import batchImg from '../../assets/images/practics-banner.png';
import { cx } from '@emotion/css';
import * as styles from './practice.style.js';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import Select, { components } from 'react-select';

const inputStyles = {
    width: 72,
    minWidth: 60,
    flexShrink: 0,
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '4px 6px',
    boxSizing: 'border-box',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fff',
    transition: 'border-color 0.2s',
};

const focusCSS = `
  input[type="number"]:focus {
    border-color: #80bfff !important;
    outline: none;
  }
`;

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        border: state.isFocused ? "2px solid rgb(241, 102, 2)" : "2px solid #ccc",
        boxShadow: state.isFocused ? "0px 4px 15px rgba(74, 144, 226, 0.3)" : "0px 2px 10px rgba(0, 0, 0, 0.1)",
        borderRadius: "12px",
        minHeight: "50px",
        "&:hover": { borderColor: "rgb(241, 102, 2)" },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#fef4e0',
        borderRadius: '8px',
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#f16602',
        fontWeight: 'bold',
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#f16602',
        ':hover': {
            backgroundColor: '#f16602',
            color: 'white',
        },
    }),
};

const Option = (props) => {
    return (
        <div>
            <components.Option {...props}>
                <input type="checkbox" checked={props.isSelected} onChange={() => null} />{" "}
                <label>{props.label}</label>
            </components.Option>
        </div>
    );
};

const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;
    if (!hasValue) {
        return (
            <components.ValueContainer {...props}>
                {children}
            </components.ValueContainer>
        );
    }
    return (
        <components.ValueContainer {...props}>
            {`${nbValues} topic${nbValues > 1 ? "s" : ""} selected`}
        </components.ValueContainer>
    );
};


function SubCategorySelection() {
    const [subCategories, setSubCategories] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const history = useHistory();

    const [value, setValue] = useState([1, 1]);
    const [inputStart, setInputStart] = useState('1');
    const [inputEnd, setInputEnd] = useState('1');
    const [random, setRandom] = useState(true);
    const [timePerQuestion, setTimePerQuestion] = useState(60);

    const { category, level } = location.state;
    const timeOptions = [0, 5, 10, 15, 30, 45, 60, 90, 120, 180, 300];

    const filteredQuestions = useMemo(() => {
        const selectedValues = new Set(selectedOptions.map(opt => opt.value));
        return originalQuestions.filter(q => selectedValues.has(q.subcategory));
    }, [originalQuestions, selectedOptions]);

    const noQuestionsSelected = filteredQuestions.length === 0;

    useEffect(() => {
        const total = filteredQuestions.length;
        setValue([1, total > 0 ? total : 1]);
    }, [filteredQuestions.length]);

    useEffect(() => {
        setInputStart(String(value[0]));
        setInputEnd(String(value[1]));
    }, [value]);

    function importAll(r) {
        let files = {};
        r.keys().forEach(item => { files[item.replace('./', '')] = r(item); });
        return files;
    }

    useEffect(() => {
        document.title = 'Select Sub-Category';
        const quizes = importAll(require.context("./resources/", true, /\.(json)$/));
        const keys = Object.keys(quizes);

        let allQuestions = [];
        for (let i = 0; i < keys.length; i++) {
            if (keys[i].includes(category) && keys[i].includes('Grade' + level)) {
                const quizData = quizes[keys[i]];
                const questions = quizData.questions || quizData;
                allQuestions = allQuestions.concat(questions);
            }
        }

        setOriginalQuestions(allQuestions);
        const uniqueSubCategories = [...new Set(allQuestions.map(q => q.subcategory))].filter(Boolean);
        const options = uniqueSubCategories.map(sub => ({ value: sub, label: sub }));
        setSubCategories(options);
        setIsLoading(false);

    }, [category, level]);

    const handleSelectChange = (selected) => {
        if (selected && selected.length > 0) {
            if (selected[selected.length - 1].value === 'select-all') {
                if (selected.length - 1 === subCategories.length) {
                    setSelectedOptions([]);
                } else {
                    setSelectedOptions(subCategories);
                }
                return;
            }
        }
        setSelectedOptions(selected);
    };

    const formatTimeDisplay = (seconds) => {
        if (seconds === 0) return 'No time limit';
        if (seconds >= 60) {
            const minutes = seconds / 60;
            return `${minutes === Math.floor(minutes) ? minutes : minutes.toFixed(1)} min`;
        }
        return `${seconds} sec`;
    };

    const formatTotalTime = (seconds) => {
        if (seconds === 0) return 'No time limit';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        if (m > 0 && s > 0) return `${m} min ${s} sec`;
        if (m > 0) return `${m} min`;
        return `${s} sec`;
    };

    const randomme = () => setRandom(!random);
    const handleTimeChange = (time) => setTimePerQuestion(parseInt(time));
    const handleChange = (change) => setValue(change);

    const handleStartInputChange = (e) => setInputStart(e.target.value);
    const handleStartBlur = () => {
        let val = parseInt(inputStart, 10);
        if (isNaN(val) || val < 1) val = 1;
        if (val >= value[1]) val = value[1] > 1 ? value[1] - 1 : 1;
        setValue([val, value[1]]);
    };

    const handleEndInputChange = (e) => setInputEnd(e.target.value);
    const handleEndBlur = () => {
        let val = parseInt(inputEnd, 10);
        const total = filteredQuestions.length;
        if (isNaN(val) || val > total) val = total;
        if (val < value[0]) val = value[0];
        setValue([value[0], val]);
    };

    const startPractice = () => {
        let questionsToPractice = filteredQuestions.slice(value[0] - 1, value[1]);
        if (random) {
            questionsToPractice.sort(() => Math.random() - 0.5);
        }

        const quizData = {
            completed: 0,
            eventtype: 'Practice',
            evenstartdate: '2020-0-31',
            levels: ['Grade' + level],
            category: category,
            totalquestions: questionsToPractice.length,
            level: 'Grade' + level,
            name: `${category} - Custom Practice`,
            eventdisplay: '',
            qualificationno: questionsToPractice.length > 0 ? questionsToPractice.length - 1 : 0,
            time: timePerQuestion,
            questions: questionsToPractice,
            id: 1,
            randomize: random,
            start_limit: value[0] - 1,
            end_limit: value[1] - 1,
        };

        history.push({
            pathname: '/practicequiz',
            state: quizData
        });
    };

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
        }
    };

    const selectOptions = [
        { value: 'select-all', label: 'Select All' },
        ...subCategories
    ];

    const numQuestionsInRange = value[1] - value[0] + 1;
    const totalTimeInSeconds = timePerQuestion === 0 ? 0 : timePerQuestion * numQuestionsInRange;

    if (isLoading) {
        return (
            <div className="welcomecenter">
                <Lottie options={defaultOptions} height={400} width={400} />
            </div>
        );
    }

    return (
        <div>
            <style>{focusCSS}</style>
            <div className={cx(styles.batchBanner)}>
                <img src={batchImg} alt="" />
            </div>
            <div className={cx(styles.batchTitle)}>
                {category === 'MathBowl' ? 'Select topics for Mathematics' : `Select Sub-Categories for ${category}`}
            </div>
            <Container className="mt-4">
                <Row>
                    <div className={cx(styles.subCategoryContainer)}>
                        <Select
                            isMulti
                            name="subcategories"
                            options={selectOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={handleSelectChange}
                            value={selectedOptions}
                            styles={selectStyles}
                            placeholder={category === 'MathBowl' ? 'Choose topics' : 'Choose sub-categories...'}
                            components={{ Option, ValueContainer }}
                            hideSelectedOptions={false}
                            closeMenuOnSelect={false}
                        />

                        <div className={cx(styles.settingsPanel, 'mt-4')}>
                            <div className={cx(styles.settingsRow)}>
                                <span className={cx(styles.settingsLabel)}>Shuffle Questions</span>
                                <label className={cx(styles.shuffleSwitch)}>
                                    <input type="checkbox" onChange={randomme} checked={random} />
                                    <span className="slider"></span>
                                </label>
                            </div>

                            <div className={cx(styles.settingsRow)}>
                                <span className={cx(styles.settingsLabel)}>No. of Questions</span>
                                <div className={cx(styles.sliderContainer)}>
                                    <input type="number" value={noQuestionsSelected ? '-' : inputStart} onChange={handleStartInputChange} onBlur={handleStartBlur} style={inputStyles} disabled={noQuestionsSelected} />
                                    <Slider
                                        trackStyle={{ height: 10, backgroundColor: '#f16602' }}
                                        handleStyle={{ height: 20, width: 20, borderColor: '#f16602' }}
                                        railStyle={{ height: 10 }}
                                        value={value}
                                        range={[1, filteredQuestions.length || 1]}
                                        min={1}
                                        max={filteredQuestions.length || 1}
                                        onChange={handleChange}
                                        style={{ flex: '1 1 0', minWidth: 120, width: 120, margin: '0 15px' }}
                                        disabled={noQuestionsSelected}
                                    />
                                    <input type="number" value={noQuestionsSelected ? '-' : inputEnd} onChange={handleEndInputChange} onBlur={handleEndBlur} style={inputStyles} disabled={noQuestionsSelected} />
                                </div>
                            </div>

                            <div className={cx(styles.settingsRow)}>
                                <span className={cx(styles.settingsLabel)}>Time per question</span>
                                <div className={cx(styles.timeSelector)}>
                                    <select id="time-select" value={timePerQuestion} onChange={(e) => handleTimeChange(e.target.value)} required>
                                        {timeOptions.map((time) => (
                                            <option key={time} value={time}>
                                                {formatTimeDisplay(time)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className={cx(styles.totalTimeDisplay)}>
                                Total Session Time: {noQuestionsSelected ? '-' : formatTotalTime(totalTimeInSeconds)}
                            </div>
                        </div>

                        <Button className={cx(styles.startPracticeButton, 'mt-3')} onClick={startPractice} disabled={noQuestionsSelected}>
                            Start Practice
                        </Button>
                    </div>
                </Row>
            </Container>
        </div>
    );
}

export default SubCategorySelection;
